import React, { PropsWithChildren } from 'react';
import styled from 'styled-components';
import { BoxConditionIcon, TruckIcon, MapMarkerIcon } from '../../../../assets';
import { theme } from '../../../../theme';
import { ReserveBar } from '../../../reserveBar';
import { Timer } from '../../../timer';
import { WatchButton } from '../../../watchButton';
import { SearchCard } from '../types';
import {
  Price,
  GrayUIText,
  Title,
  StyledBadge,
  FeaturedImageContainer,
  Image,
  BottomContent,
  WatchButtonContainer,
  FeaturedReserveBarTimerWrapper,
  StyledDeliveryPill,
  SearchCardContainerBase,
} from './styles';

const Container = styled(SearchCardContainerBase)`
  max-width: 165px;
  display: grid;
  grid-template-rows: minmax(136px, 1fr) repeat(auto-fill, 169px);
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacings[1].rem};
  margin: ${theme.spacings[2].rem};
  width: 149px;
  justify-content: space-between;
`;

const StyledTimer = styled(Timer)`
  margin-left: 2px;
`;

const FlexContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacings[2].rem};
`;

const StyledPrice = styled(Price)`
  ${theme.typography.headings[6]['sm-lg']};
  min-height: 34px;
`;

const BadgesContainer = styled.div`
  position: absolute;
  left: 0;
  top: ${theme.spacings[1].rem};
  display: flex;
  flex-direction: column;
`;

export const BaseFeaturedSearchCard: React.FC<
  PropsWithChildren<Partial<SearchCard<'Default'>>>
> = ({
  badgeLabel,
  badgeType,
  imageUrl,
  title,
  price,
  condition,
  isDelivery,
  location,
  href,
  shippingDiscount,
  children,
  lotId,
  isWatched,
  onWatchButtonClick,
  onClick,
  timeLeft,
  isWatchable,
  reserveStatus,
  reserveStatusMapping,
}) => {
  return (
    <Container
      key={lotId?.toString()}
      id={lotId?.toString()}
      href={href}
      onClick={onClick}
    >
      <FeaturedImageContainer>
        <Image src={imageUrl} alt=""></Image>

        <BadgesContainer>
          {badgeLabel && <StyledBadge label={badgeLabel} type={badgeType} />}
        </BadgesContainer>

        {!!shippingDiscount && (
          <StyledDeliveryPill
            backgroundColor={theme.colors.primary.graysBlue}
            color={theme.colors.primary.white}
          >
            {shippingDiscount}
          </StyledDeliveryPill>
        )}

        {isWatchable === true && isWatched !== undefined && (
          <WatchButtonContainer>
            <WatchButton
              lotId={lotId}
              onClick={onWatchButtonClick}
              width={24}
              filled={isWatched}
            />
          </WatchButtonContainer>
        )}
      </FeaturedImageContainer>
      {children ? (
        children
      ) : (
        <Content>
          <Title>{title}</Title>
          <FlexContainer>
            <FeaturedReserveBarTimerWrapper>
              {reserveStatus && (
                <ReserveBar
                  reserveStatus={reserveStatus}
                  reserveStatusMapping={reserveStatusMapping}
                />
              )}
            </FeaturedReserveBarTimerWrapper>
            <StyledTimer format="short" closingTime={timeLeft} />
            <StyledPrice>{price}</StyledPrice>
            <BottomContent>
              <GrayUIText>
                <BoxConditionIcon
                  width={16}
                  fill={theme.colors.neutrals.solidGrays[3]}
                />
                {condition}
              </GrayUIText>
              {(condition as string).length < 13 && (
                <GrayUIText>
                  {isDelivery ? (
                    <>
                      <TruckIcon
                        width={16}
                        fill={theme.colors.neutrals.solidGrays[3]}
                      />
                      Delivery
                    </>
                  ) : (
                    <>
                      <MapMarkerIcon
                        width={16}
                        fill={theme.colors.neutrals.solidGrays[3]}
                      />
                      {location}
                    </>
                  )}
                </GrayUIText>
              )}
            </BottomContent>
          </FlexContainer>
        </Content>
      )}
    </Container>
  );
};
